<template>
  <div class="container">
    <h2>Faturação por País</h2>
    <h4>Configuração de preços e taxas de importação</h4>

    <div class="dashboard">
      <!-- CARD PRINCIPAL -->
      <div class="section-card">
        <div class="section-header">
          <div class="section-header-left">
            <h5>🇺🇸 Estados Unidos da América (EUA)</h5>
            <p class="subtitle">
              Regra de ajuste de preço + taxas de importação
            </p>
          </div>
          <div class="section-header-right">
            <span
              :class="['status-badge', rule.enabled ? 'active' : 'inactive']"
            >
              {{ rule.enabled ? '● Ativo' : '● Inativo' }}
            </span>
            <label class="toggle">
              <input
                type="checkbox"
                v-model="rule.enabled"
                @change="onToggleChange"
              />
              <span class="toggle-slider"></span>
            </label>
          </div>
        </div>

        <div :class="['section-body', !rule.enabled ? 'disabled' : '']">
          <!-- CALLOUT -->
          <div v-if="!rule.enabled" class="callout warning">
            ⚠️ Esta regra está <strong>desativada</strong>. Ativa o toggle para
            aplicar o ajuste ao mercado dos EUA.
          </div>
          <div v-else class="callout success">
            ✅ Regra <strong>ativa</strong>. Os preços abaixo refletem o ajuste
            aplicado.
          </div>

          <!-- FÓRMULA -->
          <div class="formula-box">
            <p class="formula-label">FÓRMULA APLICADA</p>
            <p>
              Preço final = Preço original × <code>{{ rule.factor_x }}</code>
            </p>
            <p>
              Taxa de importação = Preço original ×
              <code>(1 − {{ rule.factor_x }})</code>
            </p>
          </div>

          <!-- PARÂMETRO -->
          <div class="form-row">
            <div class="form-group col">
              <label>Fator de preço (ex: 0.8 = 80% do preço original)</label>
              <input
                type="number"
                class="form-control"
                v-model.number="rule.factor_x"
                min="0.01"
                max="1"
                step="0.01"
              />
              <small class="text-muted"
                >Ex: 0.8 → o cliente paga 80% | 0.9 → paga 90%</small
              >
            </div>
            <div class="form-group col">
              <label>Fator resultante</label>
              <div class="factor-display">× {{ resultFactor }}</div>
            </div>
          </div>

          <!-- TABELA DE SIMULAÇÃO -->
          <h6 class="section-label">SIMULAÇÃO DE PRODUTOS</h6>
          <div class="table-responsive">
            <table class="table table-hover">
              <thead>
                <tr>
                  <th>Produto</th>
                  <th>Preço Original</th>
                  <th></th>
                  <th>Preço Final (EUA)</th>
                  <th>Taxa de Importação</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(prod, index) in products" :key="index">
                  <td>{{ prod.name }}</td>
                  <td>${{ prod.price.toFixed(2) }}</td>
                  <td class="arrow">›</td>
                  <td class="td-final">
                    ${{ (prod.price * resultFactor).toFixed(2) }}
                  </td>
                  <td class="td-import">
                    ${{ (prod.price - prod.price * resultFactor).toFixed(2) }}
                  </td>
                  <td>
                    <button class="btn-remove" @click="removeProduct(index)">
                      ✕
                    </button>
                  </td>
                </tr>
                <tr class="total-row" v-if="products.length > 0">
                  <td><strong>TOTAL</strong></td>
                  <td>
                    <strong>${{ totalOriginal.toFixed(2) }}</strong>
                  </td>
                  <td></td>
                  <td class="td-final">
                    <strong>${{ totalFinal.toFixed(2) }}</strong>
                  </td>
                  <td class="td-import">
                    <strong>${{ totalImport.toFixed(2) }}</strong>
                  </td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- ADICIONAR PRODUTO -->
          <div class="add-product-row">
            <div class="form-group">
              <label>Nome do produto</label>
              <input
                type="text"
                class="form-control"
                v-model="newProduct.name"
                placeholder="Ex: Produto C"
              />
            </div>
            <div class="form-group">
              <label>Preço (USD)</label>
              <input
                type="number"
                class="form-control"
                v-model.number="newProduct.price"
                placeholder="0.00"
                min="0"
                step="0.01"
              />
            </div>
            <button class="btn btn-secondary" @click="addProduct">
              + Adicionar
            </button>
          </div>
        </div>

        <!-- BOTÕES -->
        <div class="btn-row">
          <button class="btn btn-secondary" @click="resetDefaults">
            Repor predefinições
          </button>
          <button
            class="btn btn-primary"
            @click="saveConfig"
            :disabled="loading"
          >
            {{ loading ? 'A guardar...' : 'Guardar configuração' }}
          </button>
        </div>
      </div>

      <!-- HISTÓRICO -->
      <div class="section-card" style="margin-top: 20px">
        <div class="section-header">
          <h5>📋 Histórico de Alterações</h5>
        </div>
        <div class="section-body">
          <p v-if="logs.length === 0" class="text-muted">
            Nenhuma alteração registada ainda.
          </p>
          <div v-for="(log, index) in logs" :key="index" class="log-entry">
            <span class="log-date">{{ log.date }}</span>
            <span>
              Regra EUA
              <strong :class="log.enabled ? 'text-success' : 'text-muted'">{{
                log.enabled ? 'ativada' : 'desativada'
              }}</strong>
              — fator
              <strong
                >0.8 × {{ log.factor_x }} =
                {{ (0.8 * log.factor_x).toFixed(4) }}</strong
              >
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';

export default {
  name: 'BillingConfig',
  data() {
    return {
      API: `${this.$store.state.apiUrl}`,
      token: localStorage.getItem('token'),
      loading: false,
      rule: {
        enabled: false,
        factor_x: 1.0,
      },
      products: [
        { name: 'Produto A', price: 70 },
        { name: 'Produto B', price: 50 },
      ],
      newProduct: {
        name: '',
        price: null,
      },
      logs: [],
    };
  },
  computed: {
    resultFactor() {
      return parseFloat(Number(this.rule.factor_x).toFixed(4));
    },
    totalOriginal() {
      return this.products.reduce((sum, p) => sum + p.price, 0);
    },
    totalFinal() {
      return this.products.reduce(
        (sum, p) => sum + p.price * this.resultFactor,
        0
      );
    },
    totalImport() {
      return this.totalOriginal - this.totalFinal;
    },
  },
  methods: {
    headers() {
      return {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.token}`,
      };
    },
    async fetchRule() {
      try {
        const { data } = await axios.get(`${this.API}moloni/billing-rules/US`, {
          headers: this.headers(),
        });
        this.rule.enabled = data.data.enabled;
        this.rule.factor_x = parseFloat(data.data.factor_x);
      } catch (err) {
        console.error('Erro ao carregar regra:', err);
        Swal.fire('Erro', 'Não foi possível carregar a configuração.', 'error');
      }
    },
    recalculate() {
      // computed trata disto automaticamente
    },
    onToggleChange() {
      // apenas visual, o save é sempre manual
    },
    addProduct() {
      if (!this.newProduct.name) {
        Swal.fire('Atenção', 'Insere o nome do produto.', 'warning');
        return;
      }
      if (!this.newProduct.price || this.newProduct.price <= 0) {
        Swal.fire('Atenção', 'Insere um preço válido.', 'warning');
        return;
      }
      this.products.push({ ...this.newProduct });
      this.newProduct = { name: '', price: null };
    },
    removeProduct(index) {
      this.products.splice(index, 1);
    },
    resetDefaults() {
      this.rule.factor_x = 1.0;
    },
    async saveConfig() {
      const confirmed = await Swal.fire({
        title: 'Guardar configuração?',
        text: `Regra EUA será ${
          this.rule.enabled ? 'ativada' : 'desativada'
        } com fator ${this.rule.factor_x}`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#ff9393',
        cancelButtonColor: '#aaa',
        confirmButtonText: 'Sim, guardar',
        cancelButtonText: 'Cancelar',
      });

      if (!confirmed.isConfirmed) return;

      try {
        this.loading = true;
        await axios.put(
          `${this.API}moloni/billing-rules/US`,
          { enabled: this.rule.enabled, factor_x: this.rule.factor_x },
          { headers: this.headers() }
        );

        this.logs.unshift({
          date: new Date().toLocaleString('pt-PT'),
          enabled: this.rule.enabled,
          factor_x: this.rule.factor_x,
        });

        Swal.fire(
          'Guardado!',
          'Configuração atualizada com sucesso.',
          'success'
        );
      } catch (err) {
        console.error(err);
        Swal.fire('Erro', 'Não foi possível guardar a configuração.', 'error');
      } finally {
        this.loading = false;
      }
    },
  },
  async created() {
    await this.fetchRule();
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800;900&display=swap');

h2 {
  margin: 50px 50px 0px 0px;
}
h4 {
  margin: 0px 50px 20px 0px;
  color: #888;
  font-size: 14px;
}

.btn {
  margin-top: 10px;
  background: rgb(255, 147, 147);
  border: none;
  box-shadow: none !important;
}
.btn:hover,
.btn:focus {
  background: rgba(255, 147, 147, 0.658);
}
.btn-secondary {
  background: #f0f0f0;
  color: #333;
}
.btn-secondary:hover {
  background: #e0e0e0;
  color: #333;
}
.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.dashboard {
  padding: 10px;
  margin-bottom: 20px;
}

.section-card {
  background: #fff;
  border-radius: 10px;
  border: 1px solid #eee;
  overflow: hidden;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: #fafafa;
  border-bottom: 1px solid #eee;
}
.section-header h5 {
  margin: 0;
  font-weight: 600;
}
.section-header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}
.subtitle {
  margin: 2px 0 0;
  font-size: 12px;
  color: #888;
}

.section-body {
  padding: 20px;
}
.section-body.disabled {
  opacity: 0.45;
  pointer-events: none;
  user-select: none;
}

/* Status badge */
.status-badge {
  font-size: 12px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 20px;
}
.status-badge.active {
  background: #d4edda;
  color: #155724;
}
.status-badge.inactive {
  background: #f0f0f0;
  color: #888;
}

/* Toggle */
.toggle {
  position: relative;
  display: inline-block;
  width: 48px;
  height: 26px;
  margin: 0;
}
.toggle input {
  opacity: 0;
  width: 0;
  height: 0;
}
.toggle-slider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background: #ccc;
  border-radius: 26px;
  transition: 0.2s;
}
.toggle-slider::before {
  content: '';
  position: absolute;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  left: 4px;
  top: 50%;
  transform: translateY(-50%);
  background: white;
  transition: 0.2s;
}
.toggle input:checked + .toggle-slider {
  background: rgb(255, 147, 147);
}
.toggle input:checked + .toggle-slider::before {
  transform: translateX(22px) translateY(-50%);
}

/* Callouts */
.callout {
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  margin-bottom: 16px;
}
.callout.warning {
  background: #fff8e1;
  border: 1px solid #ffe082;
  color: #795548;
}
.callout.success {
  background: #e8f5e9;
  border: 1px solid #a5d6a7;
  color: #2e7d32;
}

/* Fórmula */
.formula-box {
  background: #f8f8f8;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 14px 16px;
  font-size: 14px;
  color: #555;
  margin-bottom: 20px;
}
.formula-box p {
  margin: 4px 0;
}
.formula-label {
  font-size: 11px !important;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #999 !important;
  margin-bottom: 6px !important;
}
.formula-box code {
  background: #ffe0e0;
  color: #c0392b;
  padding: 1px 6px;
  border-radius: 4px;
}

/* Factor display */
.factor-display {
  font-size: 24px;
  font-weight: 700;
  color: rgb(255, 147, 147);
  padding-top: 4px;
}

/* Form row */
.form-row {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
}
.form-row .col {
  flex: 1;
}

/* Section label */
.section-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #999;
  margin-bottom: 12px;
}

/* Table */
.td-final {
  color: rgb(200, 100, 100);
  font-weight: 600;
}
.td-import {
  color: #e67e22;
}
.arrow {
  color: #ccc;
  font-size: 18px;
}
.total-row td {
  border-top: 2px solid #eee;
}
.btn-remove {
  background: none;
  border: none;
  color: #ccc;
  cursor: pointer;
  font-size: 14px;
  padding: 2px 6px;
}
.btn-remove:hover {
  color: #e74c3c;
}

/* Add product row */
.add-product-row {
  display: flex;
  gap: 16px;
  align-items: flex-end;
  margin-top: 8px;
}
.add-product-row .form-group {
  flex: 1;
  margin: 0;
}
.add-product-row .btn {
  height: 38px;
  margin-top: 0;
  white-space: nowrap;
}

/* Btn row */
.btn-row {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 20px;
  border-top: 1px solid #eee;
}

/* Log */
.log-entry {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 10px;
  background: #fafafa;
  border-radius: 6px;
  font-size: 13px;
  margin-bottom: 8px;
}
.log-date {
  color: #aaa;
  font-size: 11px;
  white-space: nowrap;
  margin-top: 2px;
}
</style>
